import { type ReactNode, useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import IconButton from '@mui/joy/IconButton'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import type { MobileOtaUpdateDetail } from '@numeo/types-main-mobile'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { mobileOtaUpdatesApi } from 'api/mobileOtaUpdates'
import { failureStageLabel } from './otaFormat'

// The hourly series is shown as 6-hour bars: 28 over the week, placed by each point's time, so a series that
// leaves out empty hours still lands in the right bars.
const HOUR_MS = 60 * 60 * 1000
const BAR_MS = 6 * HOUR_MS
const BARS = 28

function toBars(points: MobileOtaUpdateDetail['rollbacksOverTime'], now: number): { startMs: number; events: number }[] {
    const windowStart = now - BARS * BAR_MS
    const bars = Array.from({ length: BARS }, (_, i) => ({ startMs: windowStart + i * BAR_MS, events: 0 }))
    for (const point of points) {
        const index = Math.floor((point.startMs - windowStart) / BAR_MS)
        if (index >= 0 && index < BARS) bars[index].events += point.events
    }
    return bars
}

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

function Panel({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2, flex: '1 1 320px', minWidth: 0 }}>
            <Typography level="title-sm" sx={{ mb: 1.5 }}>
                {title}
            </Typography>
            {children}
        </Sheet>
    )
}

function RollbackChart({ points }: { points: MobileOtaUpdateDetail['rollbacksOverTime'] }) {
    const bars = toBars(points, Date.now())
    const total = bars.reduce((sum, bar) => sum + bar.events, 0)
    if (total === 0) return <Typography level="body-sm">No rollbacks in the last 7 days.</Typography>
    const peak = Math.max(...bars.map((bar) => bar.events))
    return (
        <Box>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                {total} in total · peak {peak} in 6 hours
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 64 }}>
                {bars.map((bar) => (
                    <Tooltip key={bar.startMs} size="sm" title={`${new Date(bar.startMs).toLocaleString()} · ${bar.events} rollbacks`}>
                        <Box
                            sx={{
                                flex: 1,
                                height: `${Math.max(3, (bar.events / peak) * 100)}%`,
                                borderRadius: '2px',
                                bgcolor: bar.events ? 'danger.400' : 'neutral.outlinedBorder',
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
            {bars.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography level="body-xs" textColor="text.tertiary">
                        {day(bars[0].startMs)}
                    </Typography>
                    <Typography level="body-xs" textColor="text.tertiary">
                        Now
                    </Typography>
                </Box>
            ) : null}
        </Box>
    )
}

function CopyId({ value, label }: { value: string; label: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        navigator.clipboard
            ?.writeText(value)
            .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
            })
            .catch((error) => console.error(`Failed to copy the ${label}`, error))
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography level="body-xs" fontFamily="code" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {value}
            </Typography>
            <Tooltip size="sm" title={copied ? 'Copied' : 'Copy'}>
                <IconButton size="sm" variant="plain" onClick={copy} aria-label={`Copy ${label}`}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                </IconButton>
            </Tooltip>
        </Box>
    )
}

export function OtaUpdateDetail({ releaseId }: { releaseId: string }) {
    const [detail, setDetail] = useState<MobileOtaUpdateDetail | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        mobileOtaUpdatesApi
            .get(releaseId)
            .then((data) => !cancelled && setDetail(data))
            .catch((error) => {
                console.error('Failed to load the OTA update', error)
                if (!cancelled) setFailed(true)
            })
        return () => {
            cancelled = true
        }
    }, [releaseId])

    if (failed) return <Alert color="danger">Could not load this update.</Alert>
    if (!detail)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size="sm" />
            </Box>
        )

    const { update } = detail

    return (
        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {update.disableReason ? (
                <Alert color="warning" variant="soft">
                    Disabled: {update.disableReason}
                </Alert>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography level="body-xs" textColor="text.tertiary">
                    Release id (the workflow's <code>release_id</code>, to act on an update that is not the newest)
                </Typography>
                <CopyId value={update.releaseId} label="release id" />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Panel title="Rollbacks, last 7 days">
                    <RollbackChart points={detail.rollbacksOverTime} />
                </Panel>
                <Panel title="Failed updates, last 7 days">
                    {detail.failures.length === 0 ? (
                        <Typography level="body-sm">No failures.</Typography>
                    ) : (
                        <Table size="sm" sx={{ '& td:last-child, & th:last-child': { textAlign: 'right' } }}>
                            <thead>
                                <tr>
                                    <th>Step</th>
                                    <th>Reason</th>
                                    <th>Events</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.failures.map((failure) => (
                                    <tr key={`${failure.stage}:${failure.reason}`}>
                                        <td>{failureStageLabel(failure.stage)}</td>
                                        <td>{failure.reason}</td>
                                        <td>{failure.events}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Panel>
                <Panel title="Crash reports">
                    {update.bundleId ? (
                        <>
                            <Typography level="body-xs" textColor="text.tertiary">
                                Crashlytics custom key <code>ota_bundle_id</code>
                            </Typography>
                            <CopyId value={update.bundleId} label="bundle id" />
                        </>
                    ) : null}
                    {detail.crashlyticsUrl ? (
                        <Button
                            component="a"
                            href={detail.crashlyticsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outlined"
                            endDecorator={<ExternalLink size={14} />}
                            sx={{ mt: 1.5 }}
                        >
                            Open Crashlytics
                        </Button>
                    ) : null}
                </Panel>
            </Box>
        </Box>
    )
}
